import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { atestadoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Atestados = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    tipo: 'medico',
    dataInicio: '',
    dataFim: '',
    observacoes: '',
    arquivo: null
  });

  // Buscar atestados do usuário
  const { data: atestadosData, isLoading } = useQuery({
    queryKey: ['meus-atestados', user?.id],
    queryFn: async () => {
      const response = await atestadoService.meusAtestados();
      return response.data;
    },
    enabled: !!user?.id,
  });

  const atestados = atestadosData?.atestados || [];

  // Upload de atestado
  const uploadMutation = useMutation({
    mutationFn: async (data) => {
      const formData = new FormData();
      formData.append('tipo', data.tipo);
      formData.append('dataInicio', data.dataInicio);
      formData.append('dataFim', data.dataFim);
      formData.append('observacoes', data.observacoes);
      formData.append('arquivo', data.arquivo);

      return await atestadoService.upload(formData);
    },
    onSuccess: () => {
      toast.success('Atestado enviado com sucesso!');
      queryClient.invalidateQueries(['meus-atestados']);
      setShowForm(false);
      setFormData({
        tipo: 'medico',
        dataInicio: '',
        dataFim: '',
        observacoes: '',
        arquivo: null
      });
    },
    onError: (error) => {
      toast.error('Erro ao enviar atestado: ' + (error.response?.data?.message || error.message));
    }
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.arquivo) {
      toast.error('Por favor, selecione um arquivo');
      return;
    }

    if (!formData.dataInicio || !formData.dataFim) {
      toast.error('Por favor, preencha as datas');
      return;
    }

    uploadMutation.mutate(formData);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'aprovado': return 'bg-green-100 text-green-800';
      case 'rejeitado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'medico': return 'Médico';
      case 'odontologico': return 'Odontológico';
      case 'psicologico': return 'Psicológico';
      default: return tipo;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Atestados</h1>
          <p className="text-gray-600">Envie seus atestados médicos para homologação</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
        >
          {showForm ? 'Cancelar' : 'Enviar Atestado'}
        </button>
      </div>

      {/* Formulário de Upload */}
      {showForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Enviar Atestado</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Atestado
                </label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                >
                  <option value="medico">Médico</option>
                  <option value="odontologico">Odontológico</option>
                  <option value="psicologico">Psicológico</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Arquivo (JPG, PNG, PDF - Máx. 5MB)
                </label>
                <input
                  type="file"
                  name="arquivo"
                  onChange={handleChange}
                  accept=".jpg,.jpeg,.png,.pdf"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Início
                </label>
                <input
                  type="date"
                  name="dataInicio"
                  value={formData.dataInicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Fim
                </label>
                <input
                  type="date"
                  name="dataFim"
                  value={formData.dataFim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Informações adicionais sobre o atestado..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={uploadMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                {uploadMutation.isPending ? 'Enviando...' : 'Enviar Atestado'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Atestados */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Meus Atestados</h2>
        </div>
        
        {atestados.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            <p>Nenhum atestado enviado ainda.</p>
            <p className="text-sm">Clique em "Enviar Atestado" para começar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Período
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data Envio
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {atestados.map((atestado) => (
                  <tr key={atestado._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {getTipoLabel(atestado.tipo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(atestado.dataInicio)} - {formatDate(atestado.dataFim)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(atestado.status)}`}>
                        {atestado.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(atestado.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {atestado.observacoes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Atestados;







